'use client'

import {
  type Virtualizer,
  useWindowVirtualizer as useTanstackWindowVirtualizer,
} from '@tanstack/react-virtual'
import type { Provider } from 'react'
import {
  createContext,
  getTypedProvider,
  getTypedRequiredUseContext,
} from '../utils/context.ts'

export type UseWindowVirtualizerProps<ItemElement extends Element> = Parameters<
  typeof useTanstackWindowVirtualizer<ItemElement>
>[0]

function useWindowVirtualizerInstance<ItemElement extends Element>(
  props: UseWindowVirtualizerProps<ItemElement>
) {
  return useTanstackWindowVirtualizer<ItemElement>(props)
}

type UseWindowVirtualizerInstanceReturn<ItemElement extends Element> =
  ReturnType<typeof useWindowVirtualizerInstance<ItemElement>>

type UseWindowVirtualizerReturn<ItemElement extends Element> = [
  UseWindowVirtualizerInstanceReturn<ItemElement>,
  Provider<Virtualizer<Window, ItemElement>>,
  boolean,
]

export function useWindowVirtualizer<ItemElement extends Element>(
  props: UseWindowVirtualizerProps<ItemElement>
) {
  const virtualizerInstance = useTanstackWindowVirtualizer<ItemElement>(props)

  return [
    virtualizerInstance,
    getTypedProvider<Virtualizer<Window, ItemElement>>(
      UntypedWindowVirtualizationProvider
    ),
    props.enabled ?? false,
  ] as UseWindowVirtualizerReturn<ItemElement>
}

const [
  UntypedWindowVirtualizationProvider,
  useUntypedWindowVirtualizationContext,
] = createContext<UseWindowVirtualizerInstanceReturn<Element>>({
  name: 'WindowVirtualizationContext',
})

export function useWindowVirtualizationContext<ItemElement extends Element>() {
  return getTypedRequiredUseContext<
    UseWindowVirtualizerInstanceReturn<ItemElement>
  >(useUntypedWindowVirtualizationContext)()
}
